import Alpine from "alpinejs"

const ROUTES_CONFIG_TRANSLATIONS = (key) => {
    const lang = document.documentElement.lang || "de"
    const translations = {
        "Plakat-Typ": {
            de: "Poster-Typ",
            en: "Poster Type",
            fr: "Type d’affiche",
            it: "Tipo di manifesto",
        },
        Texte: {
            de: "Texte",
            en: "Texts",
            fr: "Textes",
            it: "Testi",
        },
        "Bild & Logo": {
            de: "Bild & Logo",
            en: "Image & Logo",
            fr: "Image et logo",
            it: "Immagine e logo",
        },
        Vorschau: {
            de: "Vorschau",
            en: "Preview",
            fr: "Passage en revue",
            it: "Anteprima",
        },
        Vorlage: {
            de: "Vorlage",
            en: "Template",
            fr: "Modèle",
            it: "Modello",
        },
        Logo: {
            de: "Logo",
            en: "Logo",
            fr: "Logo",
            it: "Logo",
        },
    }
    return translations[key] ? translations[key][lang] || translations[key]["de"] : key
}

const ROUTES_CONFIG = {
    /*custom: {
        id: "custom",
        name: "Eigenes Plakat",
        steps: [
            { id: 1, name: ROUTES_CONFIG_TRANSLATIONS("Plakat-Typ"), component: "select-poster" },
            { id: 2, name: ROUTES_CONFIG_TRANSLATIONS("Texte"), component: "custom" },
            { id: 3, name: ROUTES_CONFIG_TRANSLATIONS("Bild & Logo"), component: "upload-image-logo" },
            { id: 4, name: ROUTES_CONFIG_TRANSLATIONS("Vorschau"), component: "preview" },
        ],
        validation: {
            "select-poster": (data) => data.posterType && data.termsAccepted,
            custom: (data) => data.title?.length > 0,
            "upload-image-logo": (data) => true,
            preview: (data) => true, // Preview step is always accessible after upload step
        },
    },*/
    template: {
        id: "template",
        name: "Vorlage",
        steps: [
            { id: 1, name: ROUTES_CONFIG_TRANSLATIONS("Vorlage"), component: "template" },
            { id: 2, name: ROUTES_CONFIG_TRANSLATIONS("Logo"), component: "upload-logo" },
            { id: 3, name: ROUTES_CONFIG_TRANSLATIONS("Vorschau"), component: "preview" },
        ],
        validation: {
            template: (data) => data.node && data.termsAccepted,
            "upload-logo": (data) => true,
            preview: (data) => true, // Preview step is always accessible after upload step
        },
    },
}

Alpine.data("plakatgenerator", () => ({
    activeStep: 1,
    selectedRoute: "template",
    showTermsModal: true,
    isPdfGenerating: false,
    formData: {
        posterType: "template",
        title: "",
        copy: "",
        tagCaption: "",
        node: null,
        uploadedImage: null,
        uploadedLogo: null,
        termsAccepted: false,
    },
    previewData: null,
    isLoading: false,
    apiError: null,
    updatePreview: false,

    init() {
        let debounceTimer = null
        // Check if terms modal has been seen
        const hasSeenTerms = localStorage.getItem("plakatGeneratorTermsSeen") === "true"
        this.showTermsModal = !hasSeenTerms
        this.$root.addEventListener("terms-modal-close", () => {
            localStorage.setItem("plakatGeneratorTermsSeen", "true")
            this.showTermsModal = false
        })

        this.$watch("selectedRoute", () => {
            this.activeStep = 1
            this.handleRouteChange(this.selectedRoute)
        })

        this.$watch("activeStep", () => {
            this.updateStepperUI()
            this.saveState()
        })

        this.$watch(
            "formData",
            () => {
                clearTimeout(debounceTimer)
                debounceTimer = setTimeout(() => {
                    if (this.activeStep === this.totalSteps) {
                        this.updatePreview = true
                    }
                    this.generatePreviewIfReady()
                }, 500) // 500ms delay
            },
            { deep: true },
        )

        // State restoration logic remains the same
        const saved = localStorage.getItem("plakatgenerator")
        if (saved) {
            const state = JSON.parse(saved)
            if (state.selectedRoute === this.selectedRoute) {
                this.restoreState()
            } else {
                localStorage.removeItem("plakatgenerator")
                this.formData = this.getInitialFormData()
            }
        } else {
            this.formData = this.getInitialFormData()
        }

        // Modified image selection listener
        this.$root.addEventListener("image-selected", (event) => {
            const { file, dataUrl, name, fieldName } = event.detail

            if (fieldName === "image") {
                this.formData.uploadedImage = { file, dataUrl, name }
                this.saveState()
                // Trigger preview generation for image uploads
                this.generatePreviewIfReady()
            } else if (fieldName === "logo") {
                // For logo, store without waiting for crop data
                this.formData.uploadedLogo = { file, dataUrl, name }
                this.saveState() // Save state immediately for logo uploads

                // Trigger preview generation for logo uploads
                this.generatePreviewIfReady()
            }
        })

        // Modified crop listener - only for main image
        this.$root.addEventListener("crop-updated", (event) => {
            const { fieldName, cropData } = event.detail

            if (fieldName === "image" && this.formData.uploadedImage) {
                this.formData.uploadedImage.crop = cropData
                this.saveState()

                // Trigger preview generation for image crop updates
                this.generatePreviewIfReady()
            }
        })

        // Image reset listener
        this.$root.addEventListener("image-reset", (event) => {
            const { fieldName } = event.detail

            if (fieldName === "image") {
                this.formData.uploadedImage = null
                this.saveState()
                // Trigger preview generation after image reset
                this.generatePreviewIfReady()
            } else if (fieldName === "logo") {
                this.formData.uploadedLogo = null
                this.saveState()
                // Trigger preview generation after logo reset
                this.generatePreviewIfReady()
            }
        })
    },

    // Getter methods remain the same
    get currentRoute() {
        return ROUTES_CONFIG[this.selectedRoute]
    },

    get currentStep() {
        return this.currentRoute.steps[this.activeStep - 1]
    },

    get totalSteps() {
        return this.currentRoute.steps.length
    },

    // Modified canProceed getter to handle logo differently
    get canProceed() {
        const currentComponent = this.currentStep.component
        if (currentComponent === "upload") {
            return (
                this.formData.name?.length > 0 &&
                this.formData.uploadedImage?.crop && // Main image needs crop data
                this.formData.uploadedLogo // Logo only needs to be uploaded
            )
        }
        return this.currentRoute.validation[currentComponent](this.formData)
    },

    getCropData(fieldName) {
        const input = this.$root.querySelector(`input[name="${fieldName}_crop"]`)
        try {
            return input ? JSON.parse(input.value) : null
        } catch {
            return null
        }
    },

    async prev() {
        if (this.activeStep > 1) {
            this.activeStep--
            this.updatePreview = false
            // Generate preview for the current step if ready
            await this.generatePreviewIfReady()
        }
    },

    async next() {
        if (this.canProceed) {
            if (this.activeStep < this.totalSteps) {
                const nextStep = this.activeStep + 1

                // Show loading state before moving to preview step
                if (this.currentRoute.steps[nextStep - 1].component === "preview") {
                    this.isLoading = true
                    try {
                        await this.generatePreview()
                        this.activeStep = nextStep
                        this.updatePreview = false
                    } catch (error) {
                        console.error("Preview generation failed:", error)
                        this.apiError = `Error generating preview: ${error.message}`
                    } finally {
                        this.isLoading = false
                    }
                } else {
                    this.activeStep = nextStep
                    this.updatePreview = false
                    // Generate preview for new step if ready
                    await this.generatePreviewIfReady()
                }
            }
        }
    },
    // update body class based on activeStep, can hide Hero section if not on step 1
    updateBodyClass() {
        this.activeStep !== 1
            ? document.body.classList.add("activestepper")
            : document.body.classList.remove("activestepper")
    },

    // UI Update methods
    updateStepperUI() {
        if (!this.$refs.stepper) return

        const steps = this.$refs.stepper.querySelectorAll("li")
        steps.forEach((step, index) => {
            const stepNum = index + 1
            step.classList.remove("done", "active", "disabled")

            if (stepNum < this.activeStep) {
                step.classList.add("done")
            } else if (stepNum === this.activeStep) {
                step.classList.add("active")
            } else {
                step.classList.add("disabled")
            }
        })
    },

    // Form handling methods
    handleFormUpdate(field, event) {
        this.formData[field] = event.target.type === "checkbox" ? event.target.checked : event.target.value
        this.saveState()
    },

    // Route change handler
    handleRouteChange(route) {
        this.selectedRoute = route
        this.activeStep = 1

        // Reset form data to initial state
        this.formData = this.getInitialFormData()
        this.formData.posterType = route

        // Clear localStorage
        localStorage.removeItem("plakatgenerator")

        // Update stepper UI
        this.updateStepperUI()
    },

    // Check if a step is accessible
    isStepAccessible(stepNumber) {
        return stepNumber <= this.activeStep
    },

    async goToStep(stepNumber) {
        if (stepNumber <= this.activeStep && stepNumber > 0) {
            this.activeStep = stepNumber
            this.updatePreview = this.activeStep === this.totalSteps

            // Generate preview for the current step if ready
            await this.generatePreviewIfReady()

            if (window.innerWidth <= 767) {
                setTimeout(() => {
                    const activeStep = this.$refs.stepper.querySelector(".active")
                    if (activeStep) {
                        activeStep.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "end" })
                    }
                }, 0)
            }
        }
    },

    closeTermsModal() {
        localStorage.setItem("plakatGeneratorTermsSeen", "true")
        this.showTermsModal = false
    },

    // Helper method to check if we're in an upload step
    isUploadStep() {
        const currentComponent = this.currentStep?.component
        return currentComponent === "upload-image-logo" || currentComponent === "upload-logo"
    },

    // Generate preview if all required data is ready
    async generatePreviewIfReady() {
        // Skip preview generation for template route when no node is available
        if (this.selectedRoute === "template" && !this.formData.node) {
            return
        }

        await this.generatePreview()
    },

    // State management methods
    getInitialFormData() {
        return {
            posterType: this.selectedRoute || "template",
            title: "",
            copy: "",
            tagCaption: "",
            node: null,
            uploadedImage: null,
            uploadedLogo: null,
            termsAccepted: false,
        }
    },

    saveState() {
        const stateToSave = {
            activeStep: this.activeStep,
            selectedRoute: this.selectedRoute,
            formData: this.formData,
        }
        localStorage.setItem("plakatgenerator", JSON.stringify(stateToSave))
    },

    restoreState() {
        const saved = localStorage.getItem("plakatgenerator")
        if (saved) {
            const state = JSON.parse(saved)
            this.activeStep = state.activeStep
            this.selectedRoute = state.selectedRoute
            this.formData = state.formData

            this.$nextTick(() => {
                this.updateStepperUI()
                // Generate preview for restored state if ready
                this.generatePreviewIfReady()
            })
        }
    },

    async generatePreview() {
        this.isLoading = true
        this.apiError = null

        try {
            console.log(Alpine.raw(this.formData))

            const formData = new FormData()
            formData.append("language", document.documentElement.lang || "de")

            // Handle different poster types based on the selected route
            if (this.selectedRoute === "custom") {
                // Custom route: title, copy, tagCaption?, image?
                formData.append("title", this.formData.title)

                if (this.formData.copy) {
                    formData.append("copy", this.formData.copy)
                }

                if (this.formData.tagCaption) {
                    formData.append("tagCaption", this.formData.tagCaption)
                }

                // Add main image and crop data if available
                if (this.formData.uploadedImage?.file) {
                    formData.append("image", this.formData.uploadedImage.file, this.formData.uploadedImage.file.name)
                    if (this.formData.uploadedImage.crop) {
                        formData.append("imageCrop", JSON.stringify(this.formData.uploadedImage.crop))
                    }
                }
            } else if (this.selectedRoute === "template") {
                // Template route: node
                if (this.formData.node) {
                    formData.append("node", this.formData.node)
                }
            }

            // Add the logo if available
            if (this.formData.uploadedLogo?.file && this.formData.uploadedLogo.file instanceof File) {
                formData.append("logo", this.formData.uploadedLogo.file, this.formData.uploadedLogo.file.name)
            }

            const response = await fetch(`/forms/pdf/preview/${this.selectedRoute}`, {
                method: "POST",
                body: formData,
            })

            if (!response.ok) {
                const errorText = await response.text()
                console.error("Response was not ok:", {
                    status: response.status,
                    statusText: response.statusText,
                    errorText: errorText,
                })
                throw new Error(`Network error: ${response.status} ${response.statusText} - ${errorText}`)
            }

            const data = await response.json()
            if (data.status === "success") {
                // Create an image object to ensure it's fully loaded
                const previewImage = new Image()
                const imageLoadPromise = new Promise((resolve, reject) => {
                    previewImage.onload = resolve
                    previewImage.onerror = reject
                })
                previewImage.src = `data:image/jpeg;base64,${data.imageData}`

                // Wait for image to load
                await imageLoadPromise

                // Store preview data - no pdfPath from preview endpoint
                this.previewData = {
                    previewImage: previewImage.src,
                }
            } else {
                throw new Error("Erzeugung der Vorschau fehlgeschlagen")
            }
        } catch (error) {
            console.error("Fehler beim Erzeugen der Vorschau:", error)
            this.apiError = `Fehler beim Erstellen einer Vorschau. Bitte versuchen Sie es erneut. ${error.message}`
        } finally {
            this.isLoading = false
        }
    },

    async refreshPreview() {
        if (this.activeStep === this.totalSteps) {
            this.isLoading = true
            this.apiError = null

            try {
                await this.generatePreview()
                this.updatePreview = false
            } catch (error) {
                console.error("Preview refresh failed:", error)
                this.apiError = `Error refreshing preview: ${error.message}`
            } finally {
                this.isLoading = false
            }
        }
    },

    async generateFinalPdf() {
        this.isPdfGenerating = true
        this.apiError = null

        try {
            const formData = new FormData()
            formData.append("language", document.documentElement.lang || "de")

            // Handle different poster types based on the selected route
            if (this.selectedRoute === "custom") {
                // Custom route: title, copy, tagCaption?, image?, logo?
                formData.append("title", this.formData.title || "")
                formData.append("copy", this.formData.copy || "")

                if (this.formData.tagCaption) {
                    formData.append("tagCaption", this.formData.tagCaption)
                }

                // Add main image and crop data if available
                if (this.formData.uploadedImage?.file) {
                    formData.append("image", this.formData.uploadedImage.file)
                    if (this.formData.uploadedImage.crop) {
                        formData.append("imageCrop", JSON.stringify(this.formData.uploadedImage.crop))
                    }
                }

                // Add the logo if available
                if (this.formData.uploadedLogo?.file) {
                    formData.append("logo", this.formData.uploadedLogo.file)
                }
            } else if (this.selectedRoute === "template") {
                // Template route: node, logo?
                if (this.formData.node) {
                    formData.append("node", this.formData.node)
                }

                // Add the logo if available
                if (this.formData.uploadedLogo?.file) {
                    formData.append("logo", this.formData.uploadedLogo.file)
                }
            }

            const response = await fetch(`/forms/pdf/generate/${this.selectedRoute}`, {
                method: "POST",
                body: formData,
            })

            if (!response.ok) {
                const errorText = await response.text()
                console.error("PDF generation failed:", {
                    status: response.status,
                    statusText: response.statusText,
                    errorText: errorText,
                })
                throw new Error(`Network error: ${response.status} ${response.statusText} - ${errorText}`)
            }

            const data = await response.json()
            if (data.status === "success" && data.pdfPath) {
                // Store PDF data and open in new tab
                this.previewData = {
                    ...this.previewData,
                    pdfPath: data.pdfPath,
                }
                window.open(data.pdfPath, "_blank")
            } else {
                throw new Error("PDF-Erzeugung fehlgeschlagen")
            }
        } catch (error) {
            console.error("Fehler beim Erzeugen des PDFs:", error)
            this.apiError = `Fehler beim Erstellen des PDFs. Bitte versuchen Sie es erneut. ${error.message}`
        } finally {
            this.isPdfGenerating = false
        }
    },

    downloadPdf() {
        this.generateFinalPdf()
    },
}))
